import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import qs from 'qs'
import { Redirect } from 'react-router-dom'

// Utils
import { buildUrlParamObj } from '../helpers/utils'
import { redirect } from '../helpers/browser'
import { getClientIdFromToken } from '../quartermaster/src'
import { setClientToken } from '../auth'

// Actions
import { setReferrerSource } from '../actions/trackingSource'
import { fetchCustomMailer } from '../actions/customMailer'
import { flagClientForUnsubscribe } from '../actions/client'
import { setExternalRedirectUrl } from '../store/slices/auth'

import { showDisclaimer } from '../actions/layout'
import {
  unsubscribeFromHomeDigestFlow,
  unsubscribeFromHomeMarketFlow,
  unsubscribeFromHomeownerEmailsFlow,
  unsubscribeFromListingsFlow,
  unsubscribeFromCustomerFlow
} from '../constants/unsubscribe'

// Constants
import { PATH_HELPERS, SEARCH_PATH_HELPERS } from '../constants/navigation'

// Helpers
import { fireClientEventFromUtmContent } from './helpers/clientEventHelper'

const propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    hash: PropTypes.string,
    search: PropTypes.string
  })
}

const AuthCallback = ({ location }) => {
  const dispatch = useDispatch()
  const { hash, search } = location
  const redirectUrlQueryParamsFromLocalStorage = localStorage?.getItem('redirectUrl')?.split('?')?.[1]?.split('#')?.[0]

  const {
    state: resourceId,
    type: resourceIndicator,
    unsubscribe: unsubParam,
    targetEl: elementRef,
    targetPath: routeForSource,
    'referrer-source': referrerSource,
    toggleDisclaimer: disclaimerParam,
    togglePrequal,
    customMailerId,
    redirectUrl,
    externalRedirectUrl,
    zipCode,
    addNewHome,
    clientSelfUpgraded,
    customerProfileId,
    clientId,
    acceptInvite,
    feedType,
    utm_content
  } = qs.parse(redirectUrlQueryParamsFromLocalStorage ?? search, { ignoreQueryPrefix: true })

  const { state: resourceIdFromHash, customerProfileId: customerProfileIdFromHash } = qs.parse(hash, {
    ignoreQueryPrefix: true
  })

  let accessTokenParams

  if (hash) {
    accessTokenParams = buildUrlParamObj(hash, '#')
  }

  const {
    access_token: token,
    homeDigestUnsubscribe: homeDigestUnsubFragment,
    homeMarketUnsubscribe: homeMarketUnsubFragment,
    listingsUnsubscribe: listingsUnsubFragment,
    managePreferences: managePreferencesFragment,
    unsubscribe: unsubFragment,
    unsubscribeFromCustomer: unsubscribeFromCustomerFragment
  } = accessTokenParams || {}

  const parsedClientId = clientId ?? getClientIdFromToken(token)

  // Old unsub flows, to be retired but need to support until links expire
  const forHomeowners = resourceIndicator === 'homes'
  const homeDigestUnsubscribe = Boolean(homeDigestUnsubFragment)
  const homeMarketUnsubscribe = Boolean(homeMarketUnsubFragment)
  const listingsUnsubscribe = Boolean(listingsUnsubFragment)
  const unsubscribe = Boolean(unsubParam || unsubFragment)

  const managePreferences = Boolean(managePreferencesFragment)
  const shouldShowDisclaimer = Boolean(disclaimerParam)
  const shouldShowPrequal = Boolean(togglePrequal)
  const unsubscribeFromCustomer = Boolean(unsubscribeFromCustomerFragment)
  const shouldShowAcceptInvite = Boolean(acceptInvite)

  setClientToken(token)

  if (unsubscribe && listingsUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromListingsFlow))
  } else if (unsubscribe && homeMarketUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromHomeMarketFlow))
  } else if (unsubscribe && homeDigestUnsubscribe) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromHomeDigestFlow))
  } else if (unsubscribe) {
    if (forHomeowners) {
      // unsubscribe link is going to be retired
      // temporary support until old link will expire
      // usubscribe both home digest and home market
      dispatch(flagClientForUnsubscribe(unsubscribeFromHomeownerEmailsFlow))
    } else {
      dispatch(flagClientForUnsubscribe(resourceIndicator))
    }
  } else if (unsubscribeFromCustomer) {
    dispatch(flagClientForUnsubscribe(unsubscribeFromCustomerFlow))
  } else if (shouldShowDisclaimer) {
    dispatch(showDisclaimer())
  }

  if (!token || (!resourceId && !resourceIdFromHash) || !hash) {
    redirect(PATH_HELPERS.signup.buildPath())
    return null
  }

  dispatch(setReferrerSource(referrerSource))

  if (utm_content) {
    fireClientEventFromUtmContent(utm_content as string, redirectUrl as string)
  }

  if (customMailerId) {
    dispatch(fetchCustomMailer(customMailerId))
  }

  if (redirectUrl) {
    const url = new URL(redirectUrl as string)
    let pathnameWithSearch
    if (shouldShowPrequal) {
      url.searchParams.set('openPrequalModal', 'true')
    }
    if (shouldShowAcceptInvite) {
      url.searchParams.set('acceptInvite', 'true')
    }
    pathnameWithSearch = url.pathname + url.search

    return <Redirect to={pathnameWithSearch} />
  }

  if (externalRedirectUrl) {
    dispatch(setExternalRedirectUrl(externalRedirectUrl as string))

    return <Redirect to='/external-redirect-loading' />
  }

  if (resourceIndicator === 'homes') {
    const clientId = parsedClientId

    let path
    if (clientId && clientId.length === 36) {
      path = PATH_HELPERS.home.buildPath(clientId, addNewHome ? 'new' : resourceId ?? resourceIdFromHash)

      if (managePreferences || unsubscribe || unsubscribeFromCustomer) {
        path += `/preferences`
      }
    } else {
      path = `/reports/home/${addNewHome ? 'new' : resourceId ?? resourceIdFromHash}`
    }

    if (addNewHome && clientSelfUpgraded) {
      path += `?self-upgrade=true`
    }

    if (elementRef) {
      path += `#${elementRef}`
    }

    if (routeForSource) {
      path += routeForSource
    }

    if (localStorage?.getItem('redirectUrl')) {
      window.location.href = `${window.location.origin}${path}`
      return null
    }

    return <Redirect to={path} />
  }

  let path
  if (customMailerId) {
    path = PATH_HELPERS.buy.buildPath(resourceId ?? resourceIdFromHash)
  } else if (zipCode && !feedType) {
    path = PATH_HELPERS.zip.buildPath(resourceId ?? resourceIdFromHash, zipCode)
  } else {
    path = SEARCH_PATH_HELPERS.forYouTab.buildPath(
      customerProfileId ?? customerProfileIdFromHash,
      resourceId ?? resourceIdFromHash
    )
  }

  if (managePreferences || unsubscribe || unsubscribeFromCustomer) {
    path = PATH_HELPERS.buy.buildPath(resourceId ?? resourceIdFromHash, '/preferences')
  }

  if (shouldShowPrequal) {
    const url = new URL(path, window.location.origin)
    url.searchParams.set('openPrequalModal', 'true')
    path = url.pathname + url.search
  }

  if (shouldShowAcceptInvite) {
    const url = new URL(path, window.location.origin)
    url.searchParams.set('acceptInvite', 'true')
    path = url.pathname + url.search
  }

  if (localStorage?.getItem('redirectUrl')) {
    window.location.href = `${window.location.origin}${path}`
    return null
  }

  return <Redirect to={path} />
}

AuthCallback.propTypes = propTypes

export default AuthCallback
